/**
 * Pressure Role - Convert Pillar
 *
 * Conversão entre unidades de pressão usando hub-and-spoke (via pascal).
 *
 * @example
 * import { convertPressure, toBasePressure, fromBasePressure } from '@attrx/role-morphic/pressure/convert';
 *
 * convertPressure('bar', 'pascal', 1);      // 100000
 * toBasePressure('bar', 2.5);               // 250000 (pascals)
 * fromBasePressure('bar', 250000);          // 2.5
 */

import { PRESSURE_UNITS, type PressureUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unidade base para conversões (hub) */
export const PRESSURE_BASE: PressureUnit = "pascal";

// =============================================================================
// CORE CONVERSION
// =============================================================================

/**
 * Converte valor para a unidade base (pascal)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em pascals
 *
 * @example
 * toBasePressure('bar', 2.5);        // 250000
 * toBasePressure('atmosphere', 1);   // 101325
 */
export function toBasePressure(variant: PressureUnit, value: number): number {
  const config = PRESSURE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown pressure unit: ${variant}`);
  }
  return value * (config.factor ?? 1);
}

/**
 * Converte valor da unidade base (pascal) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em pascals
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBasePressure('bar', 250000);      // 2.5
 * fromBasePressure('atmosphere', 101325); // 1
 */
export function fromBasePressure(variant: PressureUnit, baseValue: number): number {
  const config = PRESSURE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown pressure unit: ${variant}`);
  }
  return baseValue / (config.factor ?? 1);
}

/**
 * Converte entre duas unidades de pressão
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertPressure('bar', 'pascal', 1);      // 100000
 * convertPressure('atmosphere', 'psi', 1);  // 14.696
 */
export function convertPressure(
  from: PressureUnit,
  to: PressureUnit,
  value: number,
): number {
  if (from === to) {
    return value;
  }
  const baseValue = toBasePressure(from, value);
  return fromBasePressure(to, baseValue);
}

/**
 * Versão safe de convertPressure que retorna Result
 *
 * @example
 * tryConvertPressure('bar', 'pascal', 1);  // { ok: true, value: 100000 }
 * tryConvertPressure('invalid', 'pascal', 1);   // { ok: false, error: '...' }
 */
export function tryConvertPressure(
  from: PressureUnit,
  to: PressureUnit,
  value: number,
): Result<number> {
  try {
    return { ok: true, value: convertPressure(from, to, value) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se uma variante existe
 *
 * @example
 * hasPressureVariant('bar');     // true
 * hasPressureVariant('invalid'); // false
 */
export function hasPressureVariant(variant: string): variant is PressureUnit {
  return variant in PRESSURE_UNITS;
}

/**
 * Retorna lista de todas as variantes disponíveis
 *
 * @example
 * getPressureVariants();  // ['megapascal', 'kilopascal', 'hectopascal', ...]
 */
export function getPressureVariants(): PressureUnit[] {
  return Object.keys(PRESSURE_UNITS) as PressureUnit[];
}
